
import { Component, ElementRef, HostListener, ViewChild, output } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalSearchResult, SearchResultBot, SearchResultDocument, SearchResultProject } from '@shared/index';
import { DialogModule } from 'primeng/dialog';
import { GlobalSearchService } from '../../services/global-search.service';

type SearchResultItem =
  | { type: 'project'; data: SearchResultProject }
  | { type: 'bot'; data: SearchResultBot }
  | { type: 'document'; data: SearchResultDocument };

@Component({
  selector: 'app-global-search',
  imports: [DialogModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss'
})
export class GlobalSearchComponent {
  @ViewChild('searchInput') public searchInput!: ElementRef<HTMLInputElement>;
  public readonly closed = output();

  public visible = false;
  public query = '';
  public results: GlobalSearchResult | null = null;
  public loading = false;
  public selectedIndex = -1;
  public flatResults: SearchResultItem[] = [];

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private searchService: GlobalSearchService,
    private router: Router
  ) { }

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.toggle();
    }
  }

  public toggle(): void {
    this.visible = !this.visible;
    if (this.visible) {
      this.query = '';
      this.results = null;
      this.selectedIndex = -1;
      this.flatResults = [];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 100);
    }
  }

  public open(): void {
    this.visible = true;
    this.query = '';
    this.results = null;
    this.selectedIndex = -1;
    this.flatResults = [];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 100);
  }

  public close(): void {
    this.visible = false;
    // TODO: The 'emit' function requires a mandatory void argument
    this.closed.emit();
  }

  public onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.query = value;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (value.trim().length === 0) {
      this.results = null;
      this.flatResults = [];
      this.selectedIndex = -1;
      return;
    }

    this.debounceTimer = setTimeout(() => {
      void this.performSearch(value);
    }, 300);
  }

  public onKeydownInInput(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.selectedIndex < this.flatResults.length - 1) {
        this.selectedIndex++;
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
      event.preventDefault();
      const item = this.flatResults[this.selectedIndex];
      if (item) {
        this.selectItem(item);
      }
    }
  }

  public selectItem(item: SearchResultItem): void {
    this.close();
    switch (item.type) {
      case 'project':
        void this.router.navigate(['/bot-admin', 'project', item.data._id]);
        break;
      case 'bot': {
        void this.router.navigate(['/bot-admin', item.data._id]);
        break;
      }
      case 'document':
        void this.router.navigate(['/bot-admin', 'project', item.data.projectId, 'documents']);
        break;
    }
  }

  public isSelected(index: number): boolean {
    return this.selectedIndex === index;
  }

  public getItemIndex(type: string, indexInGroup: number): number {
    let offset = 0;
    if (type === 'bot') {
      offset = this.results?.projects.length ?? 0;
    } else if (type === 'document') {
      offset = (this.results?.projects.length ?? 0) + (this.results?.bots.length ?? 0);
    }
    return offset + indexInGroup;
  }

  private async performSearch(query: string): Promise<void> {
    this.loading = true;
    try {
      this.results = await this.searchService.search(query);
      this.buildFlatResults();
      this.selectedIndex = this.flatResults.length > 0 ? 0 : -1;
    } catch {
      this.results = { projects: [], bots: [], documents: [] };
      this.flatResults = [];
      this.selectedIndex = -1;
    } finally {
      this.loading = false;
    }
  }

  private buildFlatResults(): void {
    this.flatResults = [];
    if (!this.results) return;
    for (const p of this.results.projects) {
      this.flatResults.push({ type: 'project', data: p });
    }
    for (const b of this.results.bots) {
      this.flatResults.push({ type: 'bot', data: b });
    }
    for (const d of this.results.documents) {
      this.flatResults.push({ type: 'document', data: d });
    }
  }

  public get hasResults(): boolean {
    return this.flatResults.length > 0;
  }

  public get hasQuery(): boolean {
    return this.query.trim().length > 0;
  }
}
